import { useState, type ReactNode } from 'react'
import { ActionIcon, Button, Menu } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconChevronDown, IconMessageChatbot } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { chamadosApi } from '@/api/chamados'
import { formatApiError } from '@/api/errors'
import type { AuthUser } from '@/api/auth'
import { useAuthStore } from '@/store/auth'
import type { EstadoDeInicioDeAtendimento } from '@/pages/chat/atendimentoChamado'

/** Role `devops`, lida como o Auditoria.tsx lê (`roles[].code`), com `role_codes` de reserva. */
export function temRoleDevops(user: AuthUser | null | undefined): boolean {
  if (!user) return false
  if (user.roles?.some((r) => r.code === 'devops')) return true
  return !!user.role_codes?.includes('devops')
}

/** O bloco `atendimento_ia` do detalhe do chamado (spec 2026-10-16 §4.1). */
export interface AtendimentoIa {
  conversation_id: number
  encerrada_em: string | null
  encerrada_por_nome?: string | null
  /** `cancelavel`: o Encerrar cancela; `false` = já tocou o repositório e bloqueia o Encerrar. */
  pedidos_em_andamento?: { id: number; estado: string; cancelavel?: boolean }[]
}

/** O que o botão precisa do chamado — as duas `ChamadoDetail` do frontend cabem aqui. */
export interface ChamadoParaAtendimentoIa {
  id: number
  protocol_number: string | null
  status: string
  atendimento_ia?: AtendimentoIa | null
}

export type EstadoDoBotao = 'iniciar' | 'continuar' | 'ver'

const ROTULO: Record<EstadoDoBotao, string> = {
  iniciar: 'Iniciar atendimento com IA',
  continuar: 'Continuar atendimento com IA',
  ver: 'Ver conversa',
}

/**
 * Sem conversa (ou com ela encerrada) é Iniciar; com o chamado já finalizado ou
 * fechado é Ver conversa; no resto, Continuar (spec 2026-10-16 §4.2, D1).
 */
export function estadoDoBotao(c: ChamadoParaAtendimentoIa): EstadoDoBotao {
  const at = c.atendimento_ia
  if (!at || at.encerrada_em) return 'iniciar'
  if (c.status === 'pending_customer_validation' || c.status === 'closed') return 'ver'
  return 'continuar'
}

/**
 * O botão do atendimento com IA (spec 2026-09-26 §4.7, estados da spec 2026-10-16).
 * Distinto do "Iniciar Atendimento" humano do TriagePanel: NÃO mexe no status do
 * chamado. Esconder o botão é conveniência — quem barra é a rota (403).
 *
 * `acoes` são os itens do menu ao lado do Continuar (Encerrar, Finalizar), que as
 * Ondas 2 e 3 penduram sem disputar este arquivo.
 */
export function IniciarAtendimentoIaButton({
  chamado,
  acoes,
  variant = 'light',
}: {
  chamado: ChamadoParaAtendimentoIa
  acoes?: ReactNode
  /** No rodapé do cartão de leitura entra como Secondary (`outline`, DS §6.1). */
  variant?: 'light' | 'outline'
}) {
  const user = useAuthStore((s: { user: AuthUser | null }) => s.user)
  const navigate = useNavigate()
  const [abrindo, setAbrindo] = useState(false)

  if (!temRoleDevops(user)) return null

  const chamadoId = chamado.id
  const estadoAtual = estadoDoBotao(chamado)

  async function abrir() {
    if (abrindo) return
    // Ver conversa só navega: sem POST, sem trilha e sem reabrir (spec §4.2).
    if (estadoAtual === 'ver' && chamado.atendimento_ia) {
      navigate(`/chat/${chamado.atendimento_ia.conversation_id}`)
      return
    }
    setAbrindo(true)
    try {
      const { data } = await chamadosApi.iniciarAtendimentoIa(chamadoId)
      const estado: EstadoDeInicioDeAtendimento = {
        iniciarAtendimentoChamado: {
          chamadoId,
          protocolo: chamado.protocol_number ?? `#${chamadoId}`,
        },
      }
      // Com resumo já visível, reabrir só abre: não gasta turno de modelo.
      navigate(`/chat/${data.conversation_id}`, data.tem_resumo ? undefined : { state: estado })
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Não foi possível iniciar o atendimento',
        message: formatApiError(err, 'Falha ao abrir a conversa com o @Atendente.'),
      })
    } finally {
      setAbrindo(false)
    }
  }

  const principal = (
    <Button
      variant={variant}
      leftSection={<IconMessageChatbot size={14} stroke={1.75} />}
      loading={abrindo}
      onClick={abrir}
    >
      {ROTULO[estadoAtual]}
    </Button>
  )

  if (estadoAtual !== 'continuar' || !acoes) return principal

  return (
    <Button.Group>
      {principal}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant={variant} size={36} aria-label="Mais ações do atendimento com IA">
            <IconChevronDown size={14} stroke={1.75} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>{acoes}</Menu.Dropdown>
      </Menu>
    </Button.Group>
  )
}
